import { useMemo } from "react";
import { PromptEditForm } from "domains/assets/constants/promptEdit";
import { useDebounce } from "domains/commons/hooks/useDebounce";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { useGetImagePromptEditCostQuery } from "infra/store/apiSlice";
import _ from "lodash";

import { skipToken } from "@reduxjs/toolkit/dist/query";

import { getPromptEditParamsBody } from "./useAssetPromptEdit";

type UseAssetPromptEditArgs =
  | {
      form: PromptEditForm;
      assetId?: string;
      skip?: never;
    }
  | {
      form?: never;
      assetId?: never;
      skip: true;
    };

export function useAssetPromptEditCu({
  form,
  assetId,
}: UseAssetPromptEditArgs): {
  isCuLoading: boolean;
  cuCost?: number;
} {
  const { selectedProject, isReader } = useTeamContext();
  const formWithoutCount = useMemo(
    () =>
      form
        ? {
            ...form,
            count: 1,
          }
        : undefined,
    [form]
  );
  const debouncedForm = useDebounce(formWithoutCount, 500);

  const body =
    assetId && formWithoutCount
      ? getPromptEditParamsBody({
          assetId,
          form: formWithoutCount,
          prompt: "cu cost",
        })
      : undefined;

  const { data: cuCostData, isFetching: isCuLoading } =
    useGetImagePromptEditCostQuery(
      assetId && body && !isReader
        ? { projectId: selectedProject.id, body }
        : skipToken
    );

  return {
    cuCost:
      assetId && cuCostData?.creativeUnitsCost !== undefined
        ? cuCostData?.creativeUnitsCost * form.count
        : undefined,
    isCuLoading:
      isCuLoading ||
      (!!cuCostData && !_.isEqual(formWithoutCount, debouncedForm)),
  };
}
